import type { DatePreset, SortDirection, TournamentSort } from './api';

/**
 * The export routes' paths (docs/specs/export.md §4). Its own module with type-only imports, so `node --test` can run
 * it; `api.ts` cannot be imported there. The routes answer a file, so the generated client has no method for them and
 * `download` reaches them (EX25).
 */

export type ExportFormat = 'csv' | 'xlsx' | 'json';

/** What the operator chose in the control: the format, and whether the file carries card IDs (EX15). */
export interface ExportChoice {
  format: ExportFormat;
  cardIds: boolean;
}

/** T1's filter and sort (EX4) — the room list's own, without `page`, `pageSize` or `anyTags`. */
export interface TournamentExportFilter {
  preset?: DatePreset;
  from?: string;
  to?: string;
  tags?: string[];
  excludeTags?: string[];
  sort?: TournamentSort;
  dir?: SortDirection;
}

export type ExportTarget =
  | { view: 'T1'; roomId: string; filter: TournamentExportFilter }
  | { view: 'T2'; tournamentId: string }
  | { view: 'L'; leaderboardId: string }
  | { view: 'R'; ratingBoardId: string }
  | { view: 'F1' | 'F2' | 'F3'; contestId: string };

/**
 * The path and query for an export. `cardIds` is always said, `true` or `false`, rather than left to the server's
 * default: the control's choice is the request. T1 copies the filter field by field, so the screen's paging (and
 * `anyTags`, the scope filters' quantifier) cannot ride along on its params object (EX4).
 */
export function exportPath(target: ExportTarget, choice: ExportChoice): string {
  const search = new URLSearchParams();
  search.set('format', choice.format);
  search.set('cardIds', choice.cardIds ? 'true' : 'false');
  const id = encodeURIComponent;

  switch (target.view) {
    case 'T1': {
      const f = target.filter;
      if (f.preset) search.set('preset', f.preset);
      if (f.from) search.set('from', f.from);
      if (f.to) search.set('to', f.to);
      for (const tag of f.tags ?? []) search.append('tags', tag);
      for (const tag of f.excludeTags ?? []) search.append('excludeTags', tag);
      if (f.sort) search.set('sort', f.sort);
      if (f.dir) search.set('dir', f.dir);
      return `/api/rooms/${id(target.roomId)}/tournaments/export?${search}`;
    }
    case 'T2':
      return `/api/tournaments/${id(target.tournamentId)}/export?${search}`;
    case 'L':
      return `/api/leaderboards/${id(target.leaderboardId)}/standings/export?${search}`;
    case 'R':
      return `/api/rating-boards/${id(target.ratingBoardId)}/standings/export?${search}`;
    case 'F1':
      return `/api/fantasy-contests/${id(target.contestId)}/standings/export?${search}`;
    case 'F2':
      return `/api/fantasy-contests/${id(target.contestId)}/teams/export?${search}`;
    case 'F3':
      return `/api/fantasy-contests/${id(target.contestId)}/pool/export?${search}`;
  }
}
